import { useEffect, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';
import { Live2DModel } from 'pixi-live2d-display/cubism4';
import { useAudioStore } from '../../stores/useAudioStore';

import { useUserStore } from '../../stores/useUserStore';
import './Live2DCanvas.css';

// PIXI.jsをwindowに設定
(window as any).PIXI = PIXI;

// registerTickerの設定
Live2DModel.registerTicker(PIXI.Ticker);

// ハートの色パレット
const HEART_COLORS = [
  0xff6b9d, // ローズピンク
  0xff8fab, // サーモンピンク
  0xc9a0dc, // ラベンダー
  0xffb7c5, // ベビーピンク
  0xff7eb3, // ホットピンク
  0xffc0cb, // ピンク
];

function drawHeart(g: PIXI.Graphics, color: number, size: number) {
  const s = size;
  g.beginFill(color);
  g.moveTo(0, -s * 0.35);
  g.bezierCurveTo(-s * 0.05, -s * 0.7, -s * 0.55, -s * 0.8, -s * 0.5, -s * 0.35);
  g.bezierCurveTo(-s * 0.45, s * 0.05, 0, s * 0.5, 0, s * 0.6);
  g.bezierCurveTo(0, s * 0.5, s * 0.45, s * 0.05, s * 0.5, -s * 0.35);
  g.bezierCurveTo(s * 0.55, -s * 0.8, s * 0.05, -s * 0.7, 0, -s * 0.35);
  g.endFill();
}

function spawnHeartBurst(app: PIXI.Application, cx: number, cy: number) {
  const count = 6 + Math.floor(Math.random() * 3); // 6-8 hearts

  for (let i = 0; i < count; i++) {
    const delay = i * 50 + Math.random() * 40;

    setTimeout(() => {
      if (!app.stage) return;

      const container = new PIXI.Container();
      const color = HEART_COLORS[Math.floor(Math.random() * HEART_COLORS.length)];
      const size = 18 + Math.random() * 14; // 18-32

      // Main heart
      const heart = new PIXI.Graphics();
      drawHeart(heart, color, size);
      container.addChild(heart);

      // Glow layer (larger, semi-transparent)
      const glow = new PIXI.Graphics();
      drawHeart(glow, color, size * 1.6);
      glow.alpha = 0.15;
      container.addChild(glow);

      // Position
      const spread = 80;
      container.x = cx + (Math.random() - 0.5) * spread;
      container.y = cy - 10 + (Math.random() - 0.5) * 30;

      const initialScale = 0.3 + Math.random() * 0.5;
      container.scale.set(initialScale);
      container.alpha = 0;

      // Animation state
      let age = 0;
      const lifetime = 60 + Math.random() * 30; // frames
      const ySpeed = -0.8 - Math.random() * 1.2;
      const xSway = (Math.random() - 0.5) * 0.04; // sway frequency
      const swayAmp = 15 + Math.random() * 15;
      const rotSpeed = (Math.random() - 0.5) * 0.03;
      const startX = container.x;

      app.stage.addChild(container);

      const animate = () => {
        age++;
        const progress = age / lifetime;

        // Fade in quickly, then fade out
        if (progress < 0.15) {
          container.alpha = progress / 0.15;
        } else {
          container.alpha = 1 - ((progress - 0.15) / 0.85);
        }

        // Float upward
        container.y += ySpeed;

        // Sinusoidal sway
        container.x = startX + Math.sin(age * xSway * Math.PI * 2) * swayAmp * progress;

        // Gentle rotation
        container.rotation += rotSpeed;

        // Slight scale up
        const scaleGrow = initialScale + progress * 0.15;
        container.scale.set(scaleGrow);

        if (age >= lifetime) {
          app.stage?.removeChild(container);
          container.destroy({ children: true });
          app.ticker.remove(animate);
        }
      };

      app.ticker.add(animate);
    }, delay);
  }
}

interface Live2DCanvasProps {
  modelPath: string;
  onModelLoad?: (model: Live2DModel) => void;
  onModelError?: (error: Error) => void;
  onShowMessage?: (message: string, duration?: number) => void;
  onNadenadeComplete?: () => void; // なでなで3回完了時のコールバック
  disabled?: boolean; // タッチ判定を無効化するかどうか
}

const Live2DCanvas: React.FC<Live2DCanvasProps> = ({
  modelPath,
  onModelLoad,
  onModelError,
  onShowMessage,
  onNadenadeComplete,
  disabled = false,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const modelRef = useRef<Live2DModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // リサイズやdisabled切り替えで初期化effectを再実行させないよう、最新値はrefで参照する
  const canvasSizeRef = useRef(canvasSize);
  canvasSizeRef.current = canvasSize;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  // コールバック関数の最新参照を保持
  const onShowMessageRef = useRef(onShowMessage);
  const onModelLoadRef = useRef(onModelLoad);
  const onModelErrorRef = useRef(onModelError);

  useEffect(() => {
    onShowMessageRef.current = onShowMessage;
    onModelLoadRef.current = onModelLoad;
    onModelErrorRef.current = onModelError;
  }, [onShowMessage, onModelLoad, onModelError]);

  // リサイズ処理（Vue版と同じ）
  useEffect(() => {
    const resizeCanvas = () => {
      const avatarArea = document.querySelector('.avatar-area');
      if (avatarArea) {
        const rect = avatarArea.getBoundingClientRect();
        // 同値なら参照を維持してリサイズeffectの不要な再実行を防ぐ
        setCanvasSize((prev) =>
          prev.width === rect.width && prev.height === rect.height
            ? prev
            : { width: rect.width, height: rect.height }
        );
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  // Live2Dモデルのリサイズ処理
  useEffect(() => {
    if (!modelRef.current || !appRef.current || canvasSize.width === 0 || canvasSize.height === 0) return;
    
    // 既存のモデルのサイズを更新
    try {
      appRef.current.renderer.resize(canvasSize.width, canvasSize.height);

      const model = modelRef.current;
      // model.width/heightは現在のscale適用後の値のため、素の寸法に戻してから
      // 再計算する（そのまま使うとscaleが二重適用されてモデルが巨大化する）
      const baseWidth = model.width / model.scale.x;
      const baseHeight = model.height / model.scale.y;
      const SCALE_FACTOR = 1.5;
      const heightScale = (canvasSize.height * SCALE_FACTOR) / baseHeight;
      const widthScale = (canvasSize.width * SCALE_FACTOR) / baseWidth;
      const scale = Math.min(heightScale, widthScale);

      model.scale.set(scale);
      model.position.set(canvasSize.width / 2, 0);
    } catch (e) {
      // リサイズエラーは無視
    }
  }, [canvasSize]);

  // サイズが確定したかどうかだけを初期化のトリガーにする（値の変化では再初期化しない）
  const sizeReady = canvasSize.width > 0 && canvasSize.height > 0;

  useEffect(() => {
    if (!canvasRef.current || !sizeReady) {
      return;
    }

    let app: PIXI.Application | null = null;
    let mounted = true;

    setIsLoading(true);

    const initLive2D = async () => {
      try {
        // PIXIアプリケーションの作成（Vue版と同じ設定）
        const { width: initWidth, height: initHeight } = canvasSizeRef.current;
        app = new PIXI.Application({
          width: initWidth,
          height: initHeight,
          backgroundAlpha: 0, // transparent の代わりに backgroundAlpha を使用
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
          antialias: true,
        });

        if (!mounted) return;

        appRef.current = app;
        canvasRef.current!.appendChild(app.view as HTMLCanvasElement);

        // Live2Dモデルの読み込み（Vue版と同じ方法）
        const model = await Live2DModel.from(modelPath);

        if (!mounted) {
          // ロード中にアンマウント/モデル切り替えされた場合は破棄（リーク防止）
          model.destroy();
          return;
        }

        modelRef.current = model;

        // Vue版と同じ配置ロジック
        // アンカーポイントを中央上部に設定（X:中央, Y:上部）
        model.anchor.set(0.5, 0);

        // ロード中にリサイズされている可能性があるため最新サイズを使う
        const { width, height } = canvasSizeRef.current;
        app.renderer.resize(width, height);

        // モデルのスケールを計算
        const SCALE_FACTOR = 1.5;
        const heightScale = (height * SCALE_FACTOR) / model.height;
        const widthScale = (width * SCALE_FACTOR) / model.width;
        const scale = Math.min(heightScale, widthScale);
        model.scale.set(scale);

        // 位置を設定（X:中央, Y:上部）
        model.position.set(width / 2, 0);


        // モデルをステージに追加
        app.stage.addChild(model);

        // アイドルモーションを開始
        if (model.internalModel?.motionManager) {
          try {
            await model.motion('Idle');
          } catch {
            // Idle motion not available, fallback to default
          }
        }

        // 胸タップカウンター
        let breastTapCount = 0;

        // タップ処理のスロットリング用
        let lastTapTime = 0;
        let isMotionPlaying = false;

        // マスコットモード: 空の体（頭以外）をつかんでウィンドウごと移動する
        let didMoveWindow = false; // 移動直後のpointertap（タップ反応）を抑制する

        const startWindowDrag = (originalEvent: MouseEvent | TouchEvent | PointerEvent) => {
          const api = window.mascotApi;
          if (!api || !('screenX' in originalEvent)) return;
          const startX = originalEvent.screenX;
          const startY = originalEvent.screenY;
          let moving = false;

          // ドラッグ中はクリック素通し判定を「受付」に固定する（MascotPage側が参照）
          document.body.dataset.mascotWindowDrag = '1';
          api.dragWindowStart();

          // ウィンドウの追従が遅れるとカーソルが一瞬canvas外に出るため、
          // PIXIのイベントではなくwindowのDOMイベントで追いかける
          const onMove = (e: PointerEvent) => {
            const dx = e.screenX - startX;
            const dy = e.screenY - startY;
            // 数px動くまでは移動扱いにしない（タップ反応と区別する）
            if (!moving && Math.hypot(dx, dy) > 6) {
              moving = true;
              didMoveWindow = true;
            }
            if (moving) api.dragWindowMove(dx, dy);
          };
          const onUp = () => {
            delete document.body.dataset.mascotWindowDrag;
            window.removeEventListener('pointermove', onMove, true);
            window.removeEventListener('pointerup', onUp, true);
          };
          window.addEventListener('pointermove', onMove, true);
          window.addEventListener('pointerup', onUp, true);
        };

        // PIXIのpointertapイベントでタップ処理（Vue版準拠）
        const onTap = (event: PIXI.InteractionEvent) => {
          // disabledの場合は何もしない
          if (disabledRef.current) {
            return;
          }
          
          // ウィンドウ移動ドラッグの直後はタップ反応させない
          if (didMoveWindow) {
            return;
          }

          // スロットリング（200ms）とモーション中のチェック
          const now = Date.now();
          if (now - lastTapTime < 200 || isMotionPlaying) {
            return;
          }
          lastTapTime = now;
          
          // PIXIの座標系を使用（Vue版と同じ方法）
          const localPos = event.data.getLocalPosition(model.parent || app?.stage);
          
          // タップエフェクトを全エリアで表示
          if (appRef.current) {
            const tapEffect = new PIXI.Graphics();
            const tapPos = event.data.getLocalPosition(appRef.current.stage);
            
            // 白い円を描画
            tapEffect.beginFill(0xffffff);
            tapEffect.drawCircle(0, 0, 20);
            tapEffect.endFill();
            
            tapEffect.x = tapPos.x;
            tapEffect.y = tapPos.y;
            tapEffect.alpha = 0.5;
            
            appRef.current.stage.addChild(tapEffect);
            
            // シンプルなフェードアウト
            let alpha = 0.5;
            const fadeOut = () => {
              alpha -= 0.02;
              tapEffect.alpha = alpha;
              tapEffect.scale.x += 0.05;
              tapEffect.scale.y += 0.05;
              
              if (alpha <= 0) {
                appRef.current?.stage.removeChild(tapEffect);
                appRef.current?.ticker.remove(fadeOut);
              }
            };
            
            appRef.current.ticker.add(fadeOut);
          }
          
          // Vue版準拠のhitTest実装
          let hitArea = null;
          try {
            // Live2DのhitTest機能を使用してエリア判定
            const hit = model.hitTest(localPos.x, localPos.y);
            if (hit && hit.length > 0) {
              // HitAreaHead または HitAreaBreasts が含まれているかチェック
              if (hit.includes('HitAreaHead')) {
                hitArea = 'HitAreaHead';
              } else if (hit.includes('HitAreaBreasts')) {
                hitArea = 'HitAreaBreasts';  
              }
            }
          } catch (e) {
            // エラーは無視
          }
          
          // Vue版準拠の処理
          if (hitArea === 'HitAreaHead') {
            // 頭エリア: タップでは反応しない（なでなで専用エリア）
            return;
            
          } else if (hitArea === 'HitAreaBreasts') {
            // 胸エリア: タップに反応する
            breastTapCount++;
            
            // モーション再生（胸エリア用：Nadenadeを除外）
            const tryPlay = async () => {
              isMotionPlaying = true;
              const groups = ['Tap', 'TapBody', 'Idle'];
              for (const g of groups) {
                try { 
                  await model.motion(g);
                  // モーション完了後にフラグを解除（約1秒後）
                  setTimeout(() => {
                    isMotionPlaying = false;
                  }, 1000);
                  return; 
                } catch (_) {}
              }
              isMotionPlaying = false;
            };
            tryPlay();
            
            // 5回に1回「どうしたの？」メッセージ
            if (breastTapCount % 5 === 0) {
              if (onShowMessageRef.current) {
                onShowMessageRef.current('どうしたの？', 3000);
              }
            } else {
              // それ以外は通常のメッセージ（音声なし）
              if (onShowMessageRef.current) {
                const breastMessages = ['もう、くすぐったいよ', 'え、えっと...', 'ちょっと恥ずかしいな...'];
                const randomMessage = breastMessages[Math.floor(Math.random() * breastMessages.length)];
                onShowMessageRef.current(randomMessage, 3000);
              }
            }
          }
        };


        // なでなで機能の実装
        let isDragging = false;
        let currentHitArea: string | null = null;
        let lastPatTime = 0;
        let patCount = 0;
        let sessionPatCount = 0; // 1セッション内のなでなで回数
        let lastSessionCompleteTime = 0; // 最後にセッション完了した時刻
        
        // ドラッグ開始
        const onDragStart = (event: PIXI.InteractionEvent) => {
          // disabledの場合は何もしない
          if (disabledRef.current) {
            return;
          }

          didMoveWindow = false;
          const localPos = event.data.getLocalPosition(model.parent || app?.stage);

          // ヒットエリア判定
          try {
            const hit = model.hitTest(localPos.x, localPos.y);
            if (hit && hit.includes('HitAreaHead')) {
              isDragging = true;
              currentHitArea = 'HitAreaHead';
              
              // Nadenadeモーション開始（単発）
              model.motion('Nadenade', 0, 3).catch(() => {
                // エラーは無視
              });
              
              // 最初のSE再生（ユーザーインタラクションがあるので再生可能）
              const { audioSettings: as1 } = useAudioStore.getState();
              const patAudio1 = new Audio('/assets/audio/se/nade.mp3');
              patAudio1.volume = as1.seVolume * as1.masterVolume;
              patAudio1.play().catch(() => {});
              lastPatTime = Date.now();
              patCount = 1;
            } else {
              // 頭以外（体）をつかんだ場合はウィンドウごと移動（マスコットモードのみ動作）
              startWindowDrag(event.data.originalEvent);
            }
          } catch (e) {
            // エラーは無視
          }
        };
        
        // ドラッグ中
        const onDragMove = (event: PIXI.InteractionEvent) => {
          if (!isDragging || currentHitArea !== 'HitAreaHead') return;
          
          const now = Date.now();
          // 1000ms間隔でSE再生
          if (now - lastPatTime > 1000) {
            // ユーザーインタラクション後なのでSE再生可能
            const { audioSettings: as2 } = useAudioStore.getState();
            const patAudio2 = new Audio('/assets/audio/se/nade.mp3');
            patAudio2.volume = as2.seVolume * as2.masterVolume;
            patAudio2.play().catch(() => {});
            lastPatTime = now;
            patCount++;
            sessionPatCount++;
            
            // 3回なでたら（約3秒）自動的に1回分の好感度アップ処理
            if (sessionPatCount >= 3 && now - lastSessionCompleteTime > 1000) {
              sessionPatCount = 0;
              lastSessionCompleteTime = now;
              
              // 好感度を上げる
              const result = useUserStore.getState().tryNadenade();

              if (result.success) {
                // 3回完了で会話イベント発生
                const nadenadeCount = useUserStore.getState().dailyNadenadeCount;
                if (nadenadeCount === 3 && onNadenadeComplete) {
                  // 会話イベントを5秒後に発生させる（なでなで終了後に自然に話しかける感じ）
                  setTimeout(() => {
                    onNadenadeComplete();
                  }, 5000);
                }
              }
            }
            
            // ハートエフェクトを表示
            if (appRef.current) {
              const heartPos = event.data.getLocalPosition(appRef.current.stage);
              spawnHeartBurst(appRef.current, heartPos.x, heartPos.y);
            }
            
            // 3回ごとにモーション再開
            if (patCount % 3 === 0) {
              model.motion('Nadenade', 0, 3).catch(() => {});
            }
          }
        };
        
        // ドラッグ終了
        const onDragEnd = () => {
          if (isDragging && currentHitArea === 'HitAreaHead') {
            // Idleモーションに戻す
            model.motion('Idle').catch(() => {
              // エラーは無視
            });
            
            // セッションカウントをリセット（次回は新しいセッションとして開始）
            sessionPatCount = 0;
          }
          isDragging = false;
          currentHitArea = null;
        };

        // モデルにイベントを設定
        model.interactive = true;
        model.on('pointertap', onTap);
        model.on('pointerdown', onDragStart);
        model.on('pointermove', onDragMove);
        model.on('pointerup', onDragEnd);
        model.on('pointerupoutside', onDragEnd);
        
        setIsLoading(false);

        // 成功コールバック
        if (onModelLoadRef.current) {
          onModelLoadRef.current(model);
        }
      } catch (error) {
        setIsLoading(false);
        
        // エラーコールバック
        if (onModelErrorRef.current) {
          onModelErrorRef.current(error as Error);
        }
      }
    };

    initLive2D();

    return () => {
      mounted = false;
      // 破棄済みオブジェクトへの参照を残さない
      modelRef.current = null;
      appRef.current = null;
      if (app) {
        try {
          app.destroy(true, { children: true, texture: true, baseTexture: true });
        } catch {
          // Cleanup error during unmount, safe to ignore
        }
      }
    };
  }, [modelPath, sizeReady]);

  // 不要になったDOM clickイベントのuseEffectを削除（PIXIのpointertapを使用するため）

  return (
    <div 
      ref={canvasRef} 
      className="live2d-canvas"
    >
      {isLoading && (
        <div className="live2d-loading">
          <p>Live2Dモデルを読み込み中...</p>
        </div>
      )}
    </div>
  );
};

export default Live2DCanvas;